import { yupResolver } from '@hookform/resolvers/yup';
import { checkboxClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import FormHelperText, { formHelperTextClasses } from '@mui/material/FormHelperText';
import MuiLink from '@mui/material/Link';
import { typographyClasses } from '@mui/material/Typography';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { get } from 'lodash-es';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import AutocompleteDepartmentsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteDepartmentsTypeahead';
import AutocompleteScopedCompaniesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteScopedCompaniesTypeahead';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import TextInput from '~components/FormFields/v2/TextInput';
import { countries } from '~constants/enums';
import { Account } from '~hooks/useAccount/models';
import {
  addressAutocompleteLabel,
  AddressItem,
} from '~hooks/useAddress/models/AddressItem';
import { SupportedAddressProvider } from '~hooks/useAddress/models/AddressProvider';
import { useGeocode } from '~hooks/useGeocode/useGeocode';
import { BasicLabel } from '~hooks/useLabels/models';
import useLabels from '~hooks/useLabels/useLabels';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  AccountDetailsDto,
  accountDetailsSchema,
  setAccountDetailsSchemaDefaultValues,
} from './accountDetailsSchema';

const blankAddress = {
  streetAddress: '',
  placeId: '',
  premise: '',
  locality: '',
  administrativeArea: '',
  postalCode: '',
  country: countries.default,
};

const apiResponseErrorsMapping = {
  name: 'name',
  'billing_address.postal_code': 'billingAddress',
} as const;

export type AccountDetailsPanelHandler = {
  onSubmit: () => Promise<AccountDetailsDto | undefined>;
};

interface AccountDetailsPanelProps {
  account?: Account;
  apiResponseErrors?: ModelError_Item[];
  isConnectedAccount?: boolean;
}

const AccountDetailsPanel = forwardRef<
  AccountDetailsPanelHandler,
  AccountDetailsPanelProps
>(function AccountDetailsPanel({ account, apiResponseErrors, isConnectedAccount }, ref) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { fetchLabelsTypeahead } = useLabels();

  const {
    clearErrors,
    control,
    formState: { errors, isSubmitted },
    handleSubmit,
    setError,
    setValue,
    trigger,
    watch,
  } = useForm<AccountDetailsDto>({
    resolver: yupResolver(accountDetailsSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setAccountDetailsSchemaDefaultValues(
      userStore,
      isConnectedAccount,
      account,
    ),
  });

  const isEditing = Boolean(account?.id);
  const isBillingAddressSame = watch('isBillingAddressSame');
  const addressError = get(errors, 'address.streetAddress.message', '');
  const selectedAddress = watch('address');

  const isPOBox = (value?: string) => /^\s*P\.?\s*O\.?\b/i.test(value ?? '');

  const [showAddressDetails, setShowAddressDetails] = useState(false);
  const [isManualAddress, setIsManualAddress] = useState(
    () =>
      Boolean(account?.address?.streetAddress) &&
      isPOBox(account?.address?.streetAddress) &&
      !account?.address?.placeId,
  );
  const [isManualBillingAddress, setIsManualBillingAddress] = useState(
    () =>
      Boolean(account?.billingAddress?.streetAddress) &&
      isPOBox(account?.billingAddress?.streetAddress) &&
      !account?.billingAddress?.placeId,
  );

  const addressProvider = SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH;
  const { getPlaces, getAddressItemByGoogleMapsPlaceId } = useGeocode({
    addressProvider,
  });

  const handleSelectedAddressChange = useCallback(
    async (item: AddressItem, fillAddressDetails?: true) => {
      if (addressProvider === SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH) {
        const addressItem = await getAddressItemByGoogleMapsPlaceId(item.placeId);

        if (fillAddressDetails) {
          setValue('address.premise', addressItem.premise);
          setValue('address.locality', addressItem.locality);
          setValue('address.administrativeArea', addressItem.administrativeArea);
          setValue('address.postalCode', addressItem.postalCode);
        }

        return addressItem;
      }

      return item;
    },
    [getAddressItemByGoogleMapsPlaceId],
  );

  useEffect(() => {
    if (isManualAddress) {
      setShowAddressDetails(true);
    } else if (selectedAddress) {
      const { premise, locality, administrativeArea, postalCode } = selectedAddress;
      const hasAnyValue = premise || locality || administrativeArea || postalCode;

      if (hasAnyValue) {
        setShowAddressDetails(true);
      }
    }
  }, [selectedAddress, isManualAddress]);

  useEffect(() => {
    setValue('isConnectedAccount', isConnectedAccount ?? false);

    if (isSubmitted) {
      trigger('address');
    }
  }, [setValue, isConnectedAccount, isSubmitted, trigger]);

  useEffect(() => {
    clearErrors();

    apiResponseErrors?.forEach((error) => {
      const apiField = error.field as keyof typeof apiResponseErrorsMapping;
      const formField = apiResponseErrorsMapping[apiField];

      if (formField) {
        setError(formField, { type: 'manual', message: error.message });
      }
    });
  }, [clearErrors, setError, apiResponseErrors]);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          handleSubmit(
            (data) => {
              resolve(data);
            },
            () => {
              resolve(undefined);
            },
          )();
        });
      },
    }),
    [handleSubmit],
  );

  return (
    <Box sx={{ display: 'grid', gap: 2, gridAutoColumns: '1fr', width: '100%' }}>
      <Box sx={{ gridColumn: 'span 2' }}>
        <AutocompleteScopedCompaniesTypeahead
          companyId={companyId}
          control={control}
          disableClearable
          isDisabled={isEditing}
          isRequired
          label={t('form_fields.company')}
          name="company"
        />
      </Box>

      <Box>
        <TextInput
          control={control}
          isRequired
          label={t('form_fields.account_name')}
          name="name"
        />
      </Box>

      <Box>
        <TextInput
          control={control}
          label={t('form_fields.external_id')}
          name="externalId"
        />
      </Box>

      <Box>
        <AutocompleteDepartmentsTypeahead
          companyId={companyId}
          control={control}
          label={t('form_fields.department')}
          name="department"
        />
      </Box>

      <Box>
        <TextInput
          control={control}
          label={t('form_fields.accounting_id')}
          name="accountingId"
        />
      </Box>

      <Box sx={{ gridColumn: 'span 2' }}>
        <AutocompleteAsyncFormField
          control={control}
          errors={errors}
          name="labels"
          multiple
          getValue={(item: BasicLabel) => item.id}
          getLabel={(item: BasicLabel) => item.name}
          asyncCallback={fetchLabelsTypeahead}
          extraRequestOptions={{ companyId }}
          label={t('form_fields.labels')}
        />
      </Box>

      <Box sx={{ gridColumn: 'span 2' }}>
        {isManualAddress ? (
          <TextInput
            control={control}
            isRequired={isConnectedAccount}
            label={t('form_fields.address')}
            name="address.streetAddress"
          />
        ) : (
          <AutocompleteAsyncFormField
            asyncCallback={getPlaces}
            control={control}
            debounceTime={500}
            errors={errors}
            getLabel={addressAutocompleteLabel}
            getValue={(item: AddressItem | null) => item?.placeId ?? ''}
            isRequired={isConnectedAccount}
            label={t('form_fields.address')}
            name="address"
            onSelect={(item: AddressItem) => handleSelectedAddressChange(item, true)}
            placeholder={t('common.address_autocomplete_hint')}
          />
        )}

        {addressError && (
          <FormHelperText error sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
            {addressError}
          </FormHelperText>
        )}

        <MuiLink
          component="button"
          type="button"
          variant="body2"
          onClick={() => {
            if (isManualAddress) {
              setValue(
                'address',
                blankAddress as unknown as AccountDetailsDto['address'],
              );
              setIsManualAddress(false);
            } else {
              setValue(
                'address',
                blankAddress as unknown as AccountDetailsDto['address'],
              );
              setShowAddressDetails(true);
              setIsManualAddress(true);
            }
          }}
          sx={{ mt: 0.5 }}
        >
          {isManualAddress
            ? t('common.search_address_instead')
            : t('common.enter_manual_address')}
        </MuiLink>
      </Box>

      <Collapse in={showAddressDetails} sx={{ gridColumn: 'span 2' }}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: 'repeat(4, 1fr)',
            width: '100%',
          }}
        >
          <Box sx={{ gridColumn: 'span 4' }}>
            <TextInput
              control={control}
              label={t('form_fields.address_2')}
              name="address.premise"
            />
          </Box>

          <Box sx={{ gridColumn: 'span 2' }}>
            <TextInput
              control={control}
              isRequired={isConnectedAccount}
              label={t('form_fields.city')}
              name="address.locality"
            />
          </Box>

          <Box>
            <TextInput
              control={control}
              label={t('form_fields.state')}
              name="address.administrativeArea"
            />
          </Box>

          <Box>
            <TextInput
              control={control}
              isRequired={isConnectedAccount}
              label={t('form_fields.zip')}
              name="address.postalCode"
            />
          </Box>

          {isManualAddress && (
            <Box sx={{ gridColumn: 'span 4' }}>
              <AutocompleteFormField
                control={control}
                errors={errors}
                name="address.country"
                list={countries.values}
                label={t('form_fields.country')}
                getValue={(item) => item.code}
                getLabel={(item) => item.name}
              />
            </Box>
          )}
        </Box>
      </Collapse>

      {(!isConnectedAccount || isEditing) && (
        <Box sx={{ gridColumn: 'span 2' }}>
          <Box sx={{ display: 'inline-block' }}>
            <CheckboxInput
              control={control}
              label={t('account.form.billing_same_as_primary_address')}
              name="isBillingAddressSame"
              sx={{
                [`& .${checkboxClasses.root}`]: { py: 0 },
                [`& .${typographyClasses.root}`]: { fontSize: '13px', fontWeight: 400 },
              }}
            />
          </Box>

          <Collapse in={!isBillingAddressSame}>
            <Box sx={{ mt: 2 }}>
              {isManualBillingAddress ? (
                <TextInput
                  control={control}
                  label={t('form_fields.billing_address')}
                  name="billingAddress.streetAddress"
                />
              ) : (
                <AutocompleteAsyncFormField
                  asyncCallback={getPlaces}
                  control={control}
                  errors={errors}
                  getLabel={addressAutocompleteLabel}
                  getValue={(item: AddressItem | null) => item?.placeId ?? ''}
                  label={t('form_fields.billing_address')}
                  name="billingAddress"
                  onSelect={handleSelectedAddressChange}
                  placeholder={t('common.address_autocomplete_hint')}
                  sx={{
                    [`& .${formHelperTextClasses.root}`]: {
                      pt: '2px',
                      fontSize: '10px',
                      fontWeight: 700,
                    },
                  }}
                />
              )}

              {isManualBillingAddress && (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    mt: 2,
                    width: '100%',
                  }}
                >
                  <Box sx={{ gridColumn: 'span 4' }}>
                    <TextInput
                      control={control}
                      label={t('form_fields.address_2')}
                      name="billingAddress.premise"
                    />
                  </Box>

                  <Box sx={{ gridColumn: 'span 2' }}>
                    <TextInput
                      control={control}
                      label={t('form_fields.city')}
                      name="billingAddress.locality"
                    />
                  </Box>

                  <Box>
                    <TextInput
                      control={control}
                      label={t('form_fields.state')}
                      name="billingAddress.administrativeArea"
                    />
                  </Box>

                  <Box>
                    <TextInput
                      control={control}
                      label={t('form_fields.zip')}
                      name="billingAddress.postalCode"
                    />
                  </Box>

                  <Box sx={{ gridColumn: 'span 4' }}>
                    <AutocompleteFormField
                      control={control}
                      errors={errors}
                      name="billingAddress.country"
                      list={countries.values}
                      label={t('form_fields.country')}
                      getValue={(item) => item.code}
                      getLabel={(item) => item.name}
                    />
                  </Box>
                </Box>
              )}

              <MuiLink
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  if (isManualBillingAddress) {
                    setValue(
                      'billingAddress',
                      blankAddress as unknown as AccountDetailsDto['billingAddress'],
                    );
                    setIsManualBillingAddress(false);
                  } else {
                    setValue(
                      'billingAddress',
                      blankAddress as unknown as AccountDetailsDto['billingAddress'],
                    );
                    setIsManualBillingAddress(true);
                  }
                }}
                sx={{ mt: 0.5 }}
              >
                {isManualBillingAddress
                  ? t('common.search_address_instead')
                  : t('common.enter_manual_address')}
              </MuiLink>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
});

export default AccountDetailsPanel;
